'use client';

import { Box, cx, css, Divider } from '@terra/ui-styled-system';
import { memo } from 'react';
import {
  MarkdownPreview,
  StreamingMarkdownPreview,
  type MarkdownPreviewCustomProps,
} from '../../markdown-preview';
import { CHAT_BODY_FONT_SIZE_PX } from './chat-nesting-layout';
import { ChatBubble } from './message/ChatBubble';
import { ChatMessage } from './message/ChatMessage';

const rootStyles = css({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: 2,
  paddingInline: 0,
  paddingBlock: 0.5,
  borderColor: 'border.accent',
  borderRadius: 'md',
  position: 'relative',
});

const assistantStyles = css({
  backgroundColor: 'transparent',
  color: 'fg.default',
  paddingBlock: 0,
  '& .wmde-markdown': {
    color: 'fg.default',
    fontWeight: '400',
    lineHeight: '22px',
  },
  '& .wmde-markdown p': {
    lineHeight: '22px',
    margin: '0 0 16px',
    '&:last-child': { marginBottom: 0 },
  },
  '& .wmde-markdown h1, & .wmde-markdown h2, & .wmde-markdown h3, & .wmde-markdown h4, & .wmde-markdown h5, & .wmde-markdown h6':
    {
      margin: '16px 0 4px !important',
      '&:first-child': { marginTop: '0 !important' },
    },
  '& .wmde-markdown ul, & .wmde-markdown ol': {
    margin: '8px 0 18px',
    '&:last-child': { marginBottom: 0 },
  },
  '& .wmde-markdown li': {
    lineHeight: '22px',
    margin: 0,
  },
  '& .wmde-markdown li + li': {
    marginTop: '8px',
  },
});

const finalAssistantOutcomeStyles = css({
  backgroundColor: 'transparent',
});

const toolInvocationStyles = css({
  gap: 1,
  paddingBlock: 1,
  backgroundColor: 'transparent',
});

const toolUpdateStyles = css({
  gap: 1,
  padding: 1,
  opacity: 0.95,
  _hover: {
    opacity: 1,
    top: 0.9,
    transition: 'all 0.2s ease-in-out',
  },
});

const guardrailViolationStyles = css({
  boxShadow: 'md',
  shadowColor: 'red.11',
});

const postSessionActionsStyles = css({
  boxShadow: 'md',
  shadowColor: 'blue.11',
});

export const MessageSkeletonCard = memo(function MessageSkeletonCard({
  header,
  footer,
  children,
  className,
  markdownSource,
  type,
  isFinalAssistantOutcome,
  isStreaming,
  objectLinks,
  components,
}: {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  markdownSource?: string;
  type?:
    | 'user'
    | 'assistant'
    | 'toolInvocation'
    | 'toolUpdate'
    | 'guardrailViolation'
    | 'postSessionActions';
  isFinalAssistantOutcome?: boolean;
  isStreaming?: boolean;
  objectLinks?: MarkdownPreviewCustomProps['objectLinks'];
  components?: MarkdownPreviewCustomProps['components'];
}) {
  const body = (
    <>
      {children}
      {markdownSource ? (
        isStreaming ? (
          <StreamingMarkdownPreview
            source={markdownSource}
            isStreaming
            compact
            fontSize={CHAT_BODY_FONT_SIZE_PX}
            objectLinks={objectLinks}
            components={components}
          />
        ) : (
          <MarkdownPreview
            source={markdownSource}
            compact
            fontSize={CHAT_BODY_FONT_SIZE_PX}
            objectLinks={objectLinks}
            components={components}
          />
        )
      ) : null}
    </>
  );

  if (type === 'user' || type === 'assistant') {
    return (
      <ChatMessage
        role={type}
        header={header}
        footer={
          footer ? (
            <>
              <Divider />
              {footer}
            </>
          ) : undefined
        }
      >
        {type === 'user' ? (
          <ChatBubble>{body}</ChatBubble>
        ) : (
          <Box
            className={cx(
              assistantStyles,
              isFinalAssistantOutcome && finalAssistantOutcomeStyles,
            )}
          >
            {body}
          </Box>
        )}
      </ChatMessage>
    );
  }

  return (
    <Box
      className={cx(
        className,
        rootStyles,
        type === 'toolInvocation' && toolInvocationStyles,
        type === 'toolUpdate' && toolUpdateStyles,
        type === 'guardrailViolation' && guardrailViolationStyles,
        type === 'postSessionActions' && postSessionActionsStyles,
      )}
    >
      {header}
      {body}
      {footer && (
        <>
          <Divider />
          {footer}
        </>
      )}
    </Box>
  );
});
